import { Alert, AlertTitle, Button } from "@mui/material";
import { getTokenExpiryInSeconds } from "@src/appV2/Auth/cognito/tokenUtils";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useCallback, useEffect, useState } from "react";

import { useImpersonation } from "./context";
import { postMessageToParent } from "./postMessageToParent";

function formatCountdown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

export function ImpersonationBanner() {
  const { isImpersonating, accessToken, endImpersonation } = useImpersonation();
  const [remainingSeconds, setRemainingSeconds] = useState<number>(() => {
    if (!accessToken) {
      return 0;
    }
    return Math.max(0, getTokenExpiryInSeconds(accessToken));
  });

  const handleEndSession = useCallback(async () => {
    endImpersonation();
    logEvent(APP_V2_APP_EVENTS.IMPERSONATION_SESSION_ENDED);
    postMessageToParent({ type: "impersonation:sessionEnded" });

    try {
      await getCognitoSingleton().signOut();
    } catch {
      // Best-effort sign out
    }
  }, [endImpersonation]);

  const isInIframe = window.parent !== window;

  useEffect(() => {
    if (!isImpersonating || !accessToken || isInIframe) {
      return undefined;
    }

    const interval = setInterval(() => {
      const seconds = Math.max(0, getTokenExpiryInSeconds(accessToken));
      setRemainingSeconds(seconds);

      if (seconds <= 0) {
        clearInterval(interval);
        endImpersonation();
        logEvent(APP_V2_APP_EVENTS.IMPERSONATION_SESSION_EXPIRED);
        postMessageToParent({ type: "impersonation:sessionExpired" });

        void (async () => {
          try {
            await getCognitoSingleton().signOut();
          } catch {
            // Best-effort sign out
          }
        })();
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [isImpersonating, accessToken, endImpersonation, isInIframe]);

  if (!isImpersonating || isInIframe) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      variant="filled"
      sx={{
        borderRadius: 0,
      }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            void handleEndSession();
          }}
        >
          End Session
        </Button>
      }
    >
      <AlertTitle>Impersonation Session</AlertTitle>
      Session expires in {formatCountdown(remainingSeconds)}
    </Alert>
  );
}
